import { createAsyncThunk } from '@reduxjs/toolkit'

import InfoService from 'routing/InfoService'
import restCountriesAPI from '../../../api/restCountriesAPI'

import {
  GET_WORLD_TIMEZONES,
  GET_AVAILABLE_COUNTRIES,
  GET_COUNTRIES,
  GET_WORLD_LANGUAGES,
} from './info.actionTypes'

export const getAvailableCountries = createAsyncThunk(GET_AVAILABLE_COUNTRIES, async () => {
  try {
    // First try the original API to maintain backward compatibility
    try {
      const response = await InfoService.getAvailableCountries()

      // Check if we got a valid response with data
      if (
        !response.data.error &&
        response.data.data &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        console.log('Available countries loaded from original API')
        return response.data.data
      }

      throw new Error('Original API returned empty or invalid data')
    } catch (originalApiError) {
      console.log(
        'Original available countries API failed, using REST Countries API instead:',
        originalApiError
      )
    }

    // Use REST Countries API as fallback - get only independent countries
    console.log('Fetching available countries from REST Countries API...')
    const countriesData = await restCountriesAPI.fetchIndependentCountries(true, [
      'name',
      'flags',
      'cca2',
      'cca3',
    ])

    if (!countriesData || !Array.isArray(countriesData) || countriesData.length === 0) {
      throw new Error('Both original and REST Countries APIs failed to return valid data')
    }

    // Transform the data to match the expected format
    const transformedData = countriesData.map((country) => ({
      printable_name: country.name.common,
      iso_3166_1_a2: country.cca2,
      iso_3166_1_a3: country.cca3,
      flag: country.flags?.png || country.flags?.svg,
    }))

    console.log(`REST Countries API returned ${transformedData.length} available countries`)
    return transformedData
  } catch (error) {
    console.error('Error fetching available countries:', error)

    // Return minimal fallback data to prevent app crashes
    return [
      { printable_name: 'United States', iso_3166_1_a2: 'US' },
      { printable_name: 'United Kingdom', iso_3166_1_a2: 'GB' },
      { printable_name: 'Canada', iso_3166_1_a2: 'CA' },
      { printable_name: 'Australia', iso_3166_1_a2: 'AU' },
      { printable_name: 'Germany', iso_3166_1_a2: 'DE' },
    ]
  }
})

export const getCountries = createAsyncThunk(GET_COUNTRIES, async () => {
  try {
    // First try the original API to maintain backward compatibility
    try {
      const response = await InfoService.getCountries()
      console.log('Raw countries response from original API:', response)

      // If we got valid data, use it
      if (
        response?.data?.data &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        return response.data.data
      }
    } catch (originalApiError) {
      console.log(
        'Original country API failed, will use REST Countries API instead:',
        originalApiError
      )
    }

    // If the original API fails or returns empty data, use the REST Countries API
    console.log('Fetching countries from REST Countries API...')
    const countriesData = await restCountriesAPI.fetchAllCountries([
      'name',
      'flags',
      'cca2',
      'cca3',
      'cioc',
    ])

    console.log(`REST Countries API returned ${countriesData.length} countries`)

    // If we still don't have data, return an empty array with fallback data
    if (!countriesData || !Array.isArray(countriesData) || countriesData.length === 0) {
      console.warn('Both country APIs failed to return valid data, using fallback data')
      return [
        { printable_name: 'United States', iso_3166_1_a2: 'US' },
        { printable_name: 'United Kingdom', iso_3166_1_a2: 'GB' },
        { printable_name: 'Canada', iso_3166_1_a2: 'CA' },
        { printable_name: 'Australia', iso_3166_1_a2: 'AU' },
        { printable_name: 'Germany', iso_3166_1_a2: 'DE' },
        { printable_name: 'France', iso_3166_1_a2: 'FR' },
        { printable_name: 'Spain', iso_3166_1_a2: 'ES' },
        { printable_name: 'Italy', iso_3166_1_a2: 'IT' },
        { printable_name: 'Brazil', iso_3166_1_a2: 'BR' },
        { printable_name: 'Mexico', iso_3166_1_a2: 'MX' },
      ]
    }

    // Transform the REST Countries data to match the format expected by our app
    const transformedData = countriesData.map((country) => ({
      printable_name: country.name.common,
      iso_3166_1_a2: country.cca2,
      iso_3166_1_a3: country.cca3,
      flag: country.flags?.png || country.flags?.svg,
    }))

    console.log('Processed countries data (sample):', transformedData.slice(0, 3)) // Debug log with just a sample

    return transformedData
  } catch (error) {
    console.error('Error fetching countries:', error)
    // Return fallback data directly
    return [
      { printable_name: 'United States', iso_3166_1_a2: 'US' },
      { printable_name: 'United Kingdom', iso_3166_1_a2: 'GB' },
      { printable_name: 'Canada', iso_3166_1_a2: 'CA' },
      { printable_name: 'Australia', iso_3166_1_a2: 'AU' },
      { printable_name: 'Germany', iso_3166_1_a2: 'DE' },
      { printable_name: 'France', iso_3166_1_a2: 'FR' },
      { printable_name: 'Spain', iso_3166_1_a2: 'ES' },
      { printable_name: 'Italy', iso_3166_1_a2: 'IT' },
      { printable_name: 'Brazil', iso_3166_1_a2: 'BR' },
      { printable_name: 'Mexico', iso_3166_1_a2: 'MX' },
    ]
  }
})

export const getWorldLanguages = createAsyncThunk(GET_WORLD_LANGUAGES, async () => {
  try {
    const response = await InfoService.getWorldLanguages()
    console.log('Raw languages response:', response) // Debug log

    // Handle different response structures
    let languagesData

    if (response.data?.data) {
      // Standard API format
      languagesData = response.data.data
    } else if (Array.isArray(response.data)) {
      // Direct array format
      languagesData = response.data
    } else {
      // Try to find data in a different location
      languagesData = response.data?.results || response.data?.items || []
    }

    console.log('Processed languages data:', languagesData) // Debug log

    if (!languagesData || (Array.isArray(languagesData) && languagesData.length === 0)) {
      console.warn('No languages data found in response')
      // Return fallback data
      return [
        { id: 'en', name: 'English' },
        { id: 'es', name: 'Spanish' },
        { id: 'fr', name: 'French' },
        { id: 'de', name: 'German' },
        { id: 'pt', name: 'Portuguese' },
      ]
    }

    return languagesData
  } catch (error) {
    console.error('Error fetching languages:', error)
    // Return fallback data on error
    return [
      { id: 'en', name: 'English' },
      { id: 'es', name: 'Spanish' },
      { id: 'fr', name: 'French' },
      { id: 'de', name: 'German' },
      { id: 'pt', name: 'Portuguese' },
    ]
  }
})

export const getWorldTimezones = createAsyncThunk(GET_WORLD_TIMEZONES, async () => {
  try {
    const response = await InfoService.getWorldTimezones()
    console.log('Raw timezones response:', response) // Debug log

    // Handle different response structures
    let timezonesData

    if (response.data?.data) {
      // Standard API format
      timezonesData = response.data.data
    } else if (Array.isArray(response.data)) {
      // Direct array format
      timezonesData = response.data
    } else {
      // Try to find data in a different location
      timezonesData = response.data?.results || response.data?.items || []
    }

    console.log('Processed timezones data:', timezonesData) // Debug log

    if (!timezonesData || (Array.isArray(timezonesData) && timezonesData.length === 0)) {
      console.warn('No timezones data found in response')
      // Return fallback data
      return [
        { id: 'UTC', name: 'UTC+0:00' },
        { id: 'America/New_York', name: 'EST (UTC-5:00)' },
        { id: 'America/Los_Angeles', name: 'PST (UTC-8:00)' },
        { id: 'Europe/London', name: 'GMT (UTC+0:00)' },
        { id: 'Europe/Paris', name: 'CET (UTC+1:00)' },
        { id: 'Asia/Tokyo', name: 'JST (UTC+9:00)' },
      ]
    }

    return timezonesData
  } catch (error) {
    console.error('Error fetching timezones:', error)
    // Return fallback data on error
    return [
      { id: 'UTC', name: 'UTC+0:00' },
      { id: 'America/New_York', name: 'EST (UTC-5:00)' },
      { id: 'America/Los_Angeles', name: 'PST (UTC-8:00)' },
      { id: 'Europe/London', name: 'GMT (UTC+0:00)' },
      { id: 'Europe/Paris', name: 'CET (UTC+1:00)' },
      { id: 'Asia/Tokyo', name: 'JST (UTC+9:00)' },
    ]
  }
})
